import { LoaderFunction } from 'react-router-dom';

// Sentry
import * as Sentry from '@sentry/react';

// GQL
import { client } from '../../../../../gql/client';
import {
  FETCH_PREDEFINED_ORDERS,
  FETCH_SUB_CAPITOLS,
} from '../../../../../gql/queries/orders/predefinedData';
import { GET_SELECTORS_DATA } from '../../../../../gql/queries/orders/orders';
import { formNaming } from '../../../../../components/forms/NewCommunicationForm/constants';
import {
  FETCH_CAPITOLS,
  FETCH_CLASSIFICATIONS,
} from '../../../../../gql/queries/orders/predefinedData';

// Type
import { AuthContextType } from '../../../../../hooks/useAuth';

// Data static
import {
  groupLanes,
  groupTracks,
  specialTagType,
} from '../../../../../data/orders';

// Compression
import { claimDraftScope } from '../../../../../utils/draftOwnership';
import { COMMUNICATION_DUPLICATE_SCOPE } from '../../../../../utils/draftScopes';
import { readOwnDraftCopy } from '../../../../../utils/ownedDraftCopy';
import { selectCommunicationOrderByIdOrderWithRelationshipIds } from '../../../../../gql/selectors/communications';
import {
  DuplicateAuthor,
  selectDuplicateAuthor,
} from '../../../../../gql/selectors/duplicateAuthor';

const loadNewCommunicationSummary = async (
  idOrder: string,
  userData: AuthContextType | null
) => {
  try {
    const orderCommunicationData =
      await selectCommunicationOrderByIdOrderWithRelationshipIds(idOrder);
    return {
      ...orderCommunicationData,
      operator: `${userData?.user?.user?.name} ${
        userData?.user?.user?.surName.split(' ')[0]
      }`,
    };
  } catch (err) {
    Sentry.captureException(err);
    console.log('> Communication Summary loading error:', err);
    return true;
  }
};

const loadFormOptions = async (userData: AuthContextType | null) => {
  try {
    if (userData?.user) {
      const [
        { data },
        {
          data: { capitols },
        },
        {
          data: { predefinedOrders },
        },
        {
          data: { classifications },
        },
        {
          data: { subCapitols },
        },
      ] = await Promise.all([
        client.query({
          query: GET_SELECTORS_DATA,
        }),
        client.query({
          query: FETCH_CAPITOLS,
        }),
        client.query({
          query: FETCH_PREDEFINED_ORDERS,
        }),
        client.query({
          query: FETCH_CLASSIFICATIONS,
        }),
        client.query({
          query: FETCH_SUB_CAPITOLS,
        }),
      ]);

      return {
        ...data,
        capitols,
        predefinedOrders,
        classifications,
        groupTracks,
        groupLanes,
        specialTagType,
        subCapitols,
      };
    }
    return {};
  } catch (err) {
    Sentry.captureException(err);
    console.log('> Communication Summary loading error:', err);
    return true;
  }
};

export const NewCommunicationDuplicateLoader: (
  userData: AuthContextType | null
) => LoaderFunction | undefined =
  (userData) =>
  async ({ params }) => {
  // Se reclama SÍNCRONO, antes de cualquier `await`: la copia del duplicado de
  // OTRO origen (atrás, URL a mano) se descarta aquí y no pisa los datos del
  // origen de esta URL. Sin `:id` (paso sin origen) no hay dueño que reclamar.
  claimDraftScope(COMMUNICATION_DUPLICATE_SCOPE, params.id);

  // El trabajo entero vive aquí dentro, íntegro. Solo ha dejado de
  // esperarse: la promesa tiene que llegar PENDIENTE al `<Await>` para que
  // el router termine la navegación al instante y el `<Fallback>` enseñe el
  // Loader DENTRO de la pantalla nueva.
  const load = async () => {
    let dataFromCommunicationToDuplicate = {};
    // «Operario» editable del duplicado: arranca en el autor de la original.
    let author: DuplicateAuthor | null = null;

    if (params.id) {
      dataFromCommunicationToDuplicate = await loadNewCommunicationSummary(
        params.id,
        userData
      );
      author = await selectDuplicateAuthor(params.id, userData);
    }

    // Pick the cached form values from local storage in case browser was reloaded
    const sessionStorageData = readOwnDraftCopy(
      formNaming.NEW_DUPLICATE_COMMUNICATION_FORM,
      params.id,
      sessionStorage,
      { allowWithoutEntity: true }
    );

    const loaderToResolve = await loadFormOptions(userData);

    if (sessionStorageData) {

      const combinedData = {
        // team field removed - users no longer have teamIDs
        ...loaderToResolve,
        ...dataFromCommunicationToDuplicate,
        // Antes del borrador: si el operario eligió otro, gana el suyo.
        ...(author && { userId: author.userId, operator: author.operator }),
        ...sessionStorageData,
        // Después: la lista es la de ahora, no la que quedó en el borrador.
        ...(author && { appUserOptions: author.appUserOptions }),
      };

      return combinedData;
    }

    const nameOperator = `${userData?.user?.user.name} ${
      userData?.user?.user.surName.split(' ')[0]
    }`;
    const combinedData = {
      // team field removed - users no longer have teamIDs
      ...loaderToResolve,
      ...dataFromCommunicationToDuplicate,
      operator: nameOperator,
      isCommunicationDuplicate: true,
      // Sin borrador: autor y nombre de la ORIGINAL, no el logueado.
      ...author,
    };

    // Prepare some default values for the form to load properly
    return combinedData;
  };

  return { data: load() };
};
